import React, { useState, useEffect, useRef } from 'react';
import { devError } from '../lib/devLog';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import PriceTag from '../components/PriceTag';
import OptimisedImage from '../components/OptimisedImage';
import Footer from '../components/Footer';
import { motion, AnimatePresence } from 'framer-motion';
import { SlidersHorizontal, X, Search, Sparkles, Loader, MessageCircle } from 'lucide-react';
import { extractEditionKind, EDITION_LABELS } from '../lib/editionKind';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Standard high-level mediums (matches the `category` field on each artwork)
const STANDARD_MEDIUMS = ['Painting', 'Photography', 'Sculpture', 'Prints', 'Drawings'];

const ShopPage = () => {
  const [searchParams] = useSearchParams();
  const [artworks, setArtworks] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  // AI Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [aiSearching, setAiSearching] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [parsedFilters, setParsedFilters] = useState(null);
  const searchTimeoutRef = useRef(null);
  
  // Filter states
  const [selectedArtist, setSelectedArtist] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPrice, setSelectedPrice] = useState('all');
  const [selectedAvailability, setSelectedAvailability] = useState('all');
  const [sortBy, setSortBy] = useState('newest'); // 'newest' | 'available-first' | 'price-low' | 'price-high'

  useEffect(() => {
    fetchData();

    // Check URL params and set initial filter
    const artistParam = searchParams.get('artist');
    if (artistParam) {
      setSelectedArtist(artistParam);
    }
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
    // Header search submits ?q=... — populate the input + auto-run the
    // AI search so the user lands on the results immediately.
    const queryParam = searchParams.get('q');
    if (queryParam) {
      setSearchQuery(queryParam);
      handleAISearch(queryParam);
    }
    // `fetchData` & `handleAISearch` are stable module-scope refs, not
    // included to avoid re-running this effect when filters change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fetch search suggestions
  useEffect(() => {
    if (searchQuery.length >= 2) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const res = await fetch(`${API_URL}/api/search/suggestions?q=${encodeURIComponent(searchQuery)}`);
          const data = await res.json();
          setSearchSuggestions(data.suggestions || []);
          setShowSuggestions(true);
        } catch (error) {
          if (process.env.NODE_ENV !== 'production') devError('Error fetching suggestions:', error);
        }
      }, 300);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchQuery]);

  const fetchData = async () => {
    try {
      // Fetch all artworks
      const artworksRes = await fetch(`${API_URL}/api/artworks`);
      const artworksData = await artworksRes.json();
      
      // Get unique artist names directly from artworks (sorted alphabetically)
      const uniqueArtists = [...new Set(artworksData.map(a => a.artistName).filter(Boolean))].sort();
      
      setArtworks(artworksData);
      setArtists(uniqueArtists); // Array of strings, not objects
      setLoading(false);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') devError('Error fetching data:', error);
      setLoading(false);
    }
  };

  // Filter artworks
  const filteredArtworks = artworks.filter(artwork => {
    if (selectedArtist !== 'all' && artwork.artistName !== selectedArtist) return false;
    if (selectedCategory !== 'all' && artwork.category !== selectedCategory) return false;
    if (selectedAvailability !== 'all') {
      if (selectedAvailability === 'available' && !artwork.available) return false;
      if (selectedAvailability === 'sold' && artwork.available) return false;
    }
    if (selectedPrice !== 'all') {
      const price = artwork.price || 0;
      if (selectedPrice === 'under-5000' && price >= 5000) return false;
      if (selectedPrice === '5000-10000' && (price < 5000 || price >= 10000)) return false;
      if (selectedPrice === 'over-10000' && price < 10000) return false;
    }
    return true;
  }).sort((a, b) => {
    // The API already returns `createdAt` desc. We re-sort on the client so
    // the visitor's chosen `sortBy` always wins regardless of the source order.
    const stamp = (w) => w.createdAt || w.updatedAt || '';
    if (sortBy === 'available-first') {
      const av = (a.available === true ? 0 : 1) - (b.available === true ? 0 : 1);
      if (av !== 0) return av;
      return (stamp(b) || '').localeCompare(stamp(a) || '');
    }
    if (sortBy === 'price-low') {
      return (a.price || 0) - (b.price || 0);
    }
    if (sortBy === 'price-high') {
      return (b.price || 0) - (a.price || 0);
    }
    // 'newest' — default. Strict createdAt desc across all rows.
    return (stamp(b) || '').localeCompare(stamp(a) || '');
  });

  const resetFilters = () => {
    setSelectedArtist('all');
    setSelectedCategory('all');
    setSelectedPrice('all');
    setSelectedAvailability('all');
    setSearchQuery('');
    setParsedFilters(null);
    fetchData(); // Reset to all artworks
  };

  // AI-powered search
  const handleAISearch = async (query) => {
    if (!query.trim()) {
      fetchData();
      return;
    }

    setAiSearching(true);
    setShowSuggestions(false);

    try {
      const response = await fetch(`${API_URL}/api/search/ai-search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query, limit: 100 })
      });

      const data = await response.json();
      
      if (data.success) {
        setArtworks(data.results);
        setParsedFilters(data.parsed_filters);
        
        // Update UI filters based on parsed results
        if (data.parsed_filters.artist) {
          setSelectedArtist(data.parsed_filters.artist);
        }
        if (data.parsed_filters.medium) {
          // Map free-form parsed medium onto our 5 standard buckets where possible
          const m = String(data.parsed_filters.medium).toLowerCase();
          const matched = STANDARD_MEDIUMS.find(c => m.includes(c.toLowerCase()) || c.toLowerCase().includes(m));
          if (matched) setSelectedCategory(matched);
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') devError('AI search error:', error);
    } finally {
      setAiSearching(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    handleAISearch(searchQuery);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground font-sans text-sm">Loading collection...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="pt-32 pb-16 px-6 max-w-[1600px] mx-auto"
      >
        <div className="max-w-4xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6 font-sans"
          >
            Available Works
          </motion.p>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl text-foreground mb-6 tracking-tight"
          >
            Contemporary Art Collection
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="font-sans text-base text-muted-foreground leading-relaxed max-w-2xl mb-8"
          >
            Discover our curated selection of contemporary artworks, prints, and photography from established and emerging artists.
          </motion.p>

          {/* AI Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="relative max-w-2xl"
          >
            <form onSubmit={handleSearchSubmit} className="relative">
              <div className="relative flex items-center">
                <Search className="absolute left-4 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder='Try "blue abstract paintings" or "Banksy prints under $10k"'
                  className="w-full pl-12 pr-32 py-4 text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:border-black transition-colors font-sans"
                />
                <button
                  type="submit"
                  disabled={aiSearching}
                  className="absolute right-2 px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2 font-medium text-sm disabled:opacity-50"
                >
                  {aiSearching ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      AI Search
                    </>
                  )}
                </button>
              </div>
            </form>

            {/* Search Suggestions */}
            <AnimatePresence>
              {showSuggestions && searchSuggestions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden"
                >
                  {searchSuggestions.map((suggestion, index) => (
                    <button
                      key={`${suggestion.type}-${suggestion.text}-${index}`}
                      onClick={() => {
                        setSearchQuery(suggestion.text);
                        setShowSuggestions(false);
                        handleAISearch(suggestion.text);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3 border-b border-gray-100 last:border-b-0"
                    >
                      <span className="text-xs px-2 py-1 bg-gray-100 rounded text-gray-600 capitalize">
                        {suggestion.type}
                      </span>
                      <span className="text-sm">{suggestion.text}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Parsed Filters Display */}
            {parsedFilters && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-3 flex flex-wrap gap-2"
              >
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  AI understood:
                </span>
                {Object.entries(parsedFilters).map(([key, value]) => {
                  if (!value || (Array.isArray(value) && value.length === 0)) return null;
                  return (
                    <span
                      key={key}
                      className="text-xs px-2 py-1 bg-black text-white rounded-full"
                    >
                      {key}: {Array.isArray(value) ? value.join(', ') : value}
                    </span>
                  );
                })}
              </motion.div>
            )}
          </motion.div>
        </div>
      </motion.section>

      {/* Filter Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="border-t border-b border-border bg-background sticky top-[80px] z-40 backdrop-blur-xl bg-background/80"
      >
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          {/* Medium tabs — All · Painting · Photography · Sculpture · Prints · Drawings */}
          <div
            className="flex items-center gap-1 sm:gap-2 overflow-x-auto scrollbar-hide -mx-6 px-6 pb-3 mb-3 border-b border-border/60"
            data-testid="medium-tabs"
          >
            {['all', ...STANDARD_MEDIUMS].map((m) => {
              const label = m === 'all' ? 'All' : m;
              const active = selectedCategory === m;
              return (
                <button
                  key={m}
                  onClick={() => setSelectedCategory(m)}
                  data-testid={`medium-tab-${m.toLowerCase()}`}
                  className={`whitespace-nowrap px-4 py-1.5 text-xs uppercase tracking-[0.2em] font-sans transition-all ${
                    active
                      ? 'bg-foreground text-background'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 text-sm font-sans font-medium text-foreground hover:text-muted-foreground transition-colors"
                data-testid="filter-toggle"
              >
                <SlidersHorizontal className="w-4 h-4" />
                Filters
              </button>
              
              {(selectedArtist !== 'all' || selectedCategory !== 'all' || selectedPrice !== 'all' || selectedAvailability !== 'all') && (
                <button
                  onClick={resetFilters}
                  className="text-xs font-sans text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                  data-testid="reset-filters"
                >
                  <X className="w-3 h-3" />
                  Clear all
                </button>
              )}
            </div>
            
            <div className="flex items-center gap-4">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="Sort artworks"
                data-testid="shop-sort-select"
                className="text-sm font-sans bg-transparent border-b border-border focus:outline-none focus:border-foreground py-1 cursor-pointer"
              >
                <option value="newest">Newest first</option>
                <option value="available-first">Available first</option>
                <option value="price-low">Price: low to high</option>
                <option value="price-high">Price: high to low</option>
              </select>
              <p className="text-sm font-sans text-muted-foreground" data-testid="shop-result-count">
                {filteredArtworks.length} {filteredArtworks.length === 1 ? 'work' : 'works'}
              </p>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-6 mt-4 border-t border-border"
            >
              {/* Artist Filter */}
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3 font-sans">
                  Artist
                </label>
                <select
                  value={selectedArtist}
                  onChange={(e) => setSelectedArtist(e.target.value)}
                  className="w-full px-3 py-2 border border-border bg-background text-sm font-sans focus:outline-none focus:border-foreground transition-colors rounded-sm"
                  data-testid="artist-filter"
                >
                  <option value="all">All Artists</option>
                  {artists.map(artistName => (
                    <option key={artistName} value={artistName}>{artistName}</option>
                  ))}
                </select>
              </div>

              {/* Medium Filter */}
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3 font-sans">
                  Medium
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-border bg-background text-sm font-sans focus:outline-none focus:border-foreground transition-colors rounded-sm"
                  data-testid="medium-filter"
                >
                  <option value="all">All Mediums</option>
                  {STANDARD_MEDIUMS.map(medium => (
                    <option key={medium} value={medium}>{medium}</option>
                  ))}
                </select>
              </div>

              {/* Price Filter */}
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3 font-sans">
                  Price Range
                </label>
                <select
                  value={selectedPrice}
                  onChange={(e) => setSelectedPrice(e.target.value)}
                  className="w-full px-3 py-2 border border-border bg-background text-sm font-sans focus:outline-none focus:border-foreground transition-colors rounded-sm"
                  data-testid="price-filter"
                >
                  <option value="all">All Prices</option>
                  <option value="under-5000">Under £5,000</option>
                  <option value="5000-10000">£5,000 - £10,000</option>
                  <option value="over-10000">Over £10,000</option>
                </select>
              </div>

              {/* Availability Filter */}
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3 font-sans">
                  Availability
                </label>
                <select
                  value={selectedAvailability}
                  onChange={(e) => setSelectedAvailability(e.target.value)}
                  className="w-full px-3 py-2 border border-border bg-background text-sm font-sans focus:outline-none focus:border-foreground transition-colors rounded-sm"
                  data-testid="availability-filter"
                >
                  <option value="all">All Works</option>
                  <option value="available">Available</option>
                  <option value="sold">Sold</option>
                </select>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Artworks Grid */}
      <section className="max-w-[1600px] mx-auto px-6 py-20">
        {filteredArtworks.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-sans text-muted-foreground">No artworks match your filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {filteredArtworks.map((artwork, index) => (
              <motion.div
                key={artwork.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ 
                  duration: 0.6,
                  delay: index * 0.05,
                  ease: [0.22, 1, 0.36, 1]
                }}
                data-testid={`artwork-card-${artwork.id}`}
              >
                <Link to={`/artwork/${artwork.slug || artwork.id}`} className="group block">
                  {/* Image */}
                  <motion.div
                    className="relative aspect-[3/4] bg-neutral-50 mb-4 overflow-hidden flex items-center justify-center"
                    whileHover={{ scale: 0.98 }}
                    transition={{ duration: 0.4 }}
                  >
                    {artwork.imageUrl ? (
                      <OptimisedImage
                        src={artwork.imageUrl}
                        alt={artwork.title}
                        width={480}
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-muted">
                        <p className="text-xs text-muted-foreground font-sans">Image Coming Soon</p>
                      </div>
                    )}

                    {/* Sculpture badge — sculpture is its own medium, never a 2D print */}
                    {artwork.category === 'Sculpture' && (
                      <div
                        className="absolute top-3 left-3 px-2.5 py-1 bg-white/90 backdrop-blur-sm text-[9px] font-sans uppercase tracking-[0.2em] text-black z-20"
                        data-testid="sculpture-badge"
                      >
                        Sculpture
                      </div>
                    )}

                    {/* Edition tier badge — Vintage / Posthumous / Co-Signed / Platinum / etc.
                        Extracted from the title (title includes the suffix for these editions). */}
                    {(() => {
                      const ed = extractEditionKind(artwork.title);
                      if (!ed) return null;
                      return (
                        <div
                          className="absolute top-3 left-3 px-2.5 py-1 bg-white/90 backdrop-blur-sm text-[9px] font-sans uppercase tracking-[0.2em] text-black z-20"
                          data-testid={`edition-badge-${ed}`}
                          style={{ top: artwork.category === 'Sculpture' ? '2.5rem' : undefined }}
                        >
                          {EDITION_LABELS[ed]}
                        </div>
                      );
                    })()}

                    {!artwork.available && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
                        <span className="px-4 py-2 bg-white/95 text-black text-xs uppercase tracking-[0.25em] font-medium">
                          {artwork.status === 'on_hold' ? 'On Hold' : 'Sold'}
                        </span>
                      </div>
                    )}
                  </motion.div>

                  {/* Details */}
                  <div className="space-y-1">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-sans">
                      {artwork.artistName}
                    </p>
                    <h3 className="font-serif text-xl text-foreground group-hover:text-muted-foreground transition-colors">
                      {artwork.title}
                    </h3>
                    {artwork.year && (
                      <p className="text-sm text-muted-foreground font-sans">{artwork.year}</p>
                    )}
                    {/* Edition info for Banksy prints */}
                    {artwork.artistName === 'Banksy' && artwork.edition && (
                      <p className="text-xs font-sans text-foreground/80 pt-1 font-medium">
                        {artwork.edition}
                      </p>
                    )}
                    <div className="flex items-center justify-between gap-3 pt-2">
                      <PriceTag
                        priceDisplay={artwork.priceDisplay}
                        vatScheme={artwork.vatScheme || 'standard'}
                        className="text-sm font-sans text-foreground"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const shareUrl = `${window.location.origin}/api/share/artwork/${artwork.id}`;
                          const message = `${artwork.title} by ${artwork.artistName} — ${shareUrl}`;
                          window.open(
                            `https://wa.me/?text=${encodeURIComponent(message)}`,
                            '_blank',
                            'noopener,noreferrer'
                          );
                        }}
                        data-testid={`artwork-card-whatsapp-${artwork.id}`}
                        aria-label={`Share ${artwork.title} on WhatsApp`}
                        title="Share on WhatsApp"
                        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-sans text-muted-foreground hover:text-[#25D366] transition-colors"
                      >
                        <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.8} />
                        Share
                      </button>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default ShopPage;
